<template>
	<div class="app--field-phone input-group">
		<select class="form-select dropdown-countries" ref="select" :disabled="disabled"
			:class="classes + (disabled ? ' form-control-readonly cursor-not-allowed' : '')" v-on:change="change"
			v-on:blur="toggle(true)" v-on:mousedown.left="toggle(!isClosed)">
			<option v-for="c in countries" :value="c.key" :title="c.name" :code="c.value"
				:selected="c.key == value.country">
				{{ c.value }}
			</option>
		</select>
		<input type="text" class="form-control" ref="input"
			:class="classes + (disabled ? ' form-control-readonly cursor-not-allowed' : '')" :disabled="disabled"
			:data-label="label" :required="required" v-validate="validate" v-on:input="input" v-on:blur="blur" />
	</div>
</template>

<script>
import Countries from './../countries'
import PhoneLib from 'google-libphonenumber'
import _find from 'lodash/find'
import _each from 'lodash/each'

export default {
	emits: ['update:modelValue'],
	props: {
		name: { type: String, required: true },
		label: { type: String, required: true },
		classes: { type: String, default: '' },
		disabled: { type: Boolean, default: false },
		required: { type: Boolean, default: false },
		country: { type: String, default: 'US' },
		extension: { type: Boolean, default: false },
		modelValue: { type: Object, default: function () { return {} } },
		validateOnBlur: { type: Boolean, default: false },
	},
	data() {
		return {
			lib: PhoneLib.PhoneNumberUtil.getInstance(),
			isClosed: true,
			formats: { E164: 0, INTERNATIONAL: 1, NATIONAL: 2, RFC3966: 3 }
		}
	},
	computed: {
		countries() { return Countries.callingCodes() },
		value: {
			get() { return { ...{ number: '', code: '', country: this.country, ext: '' }, ...this.modelValue } },
			set(v) { this.$emit('update:modelValue', v) }
		},
	},
	watch: {
		value: {
			deep: true,
			handler(value) {
				if (!value.hasOwnProperty('country') || !value.country) {
					this.value.country = (value.hasOwnProperty('code') && value.code.length > 0) ?
						_find(this.countries, ['value', value.code]).key : this.country
				}
				if (!value.hasOwnProperty('code') || !value.code) {
					this.value.code = _find(this.countries, ['key', this.value.country]).value
				}
				if (!value.hasOwnProperty('number')) {
					value.number = ''
				}
				if (!value.hasOwnProperty('ext')) {
					value.ext = ''
				}
				if (this.$refs.input && this.$refs.select) {
					this.$refs.input.value = this.format(value.number.toString() + (value.ext ? 'x' + value.ext.toString() : ''))
					this.$refs.select.value = this.value.country
				}
			}
		}
	},
	created() {
		if (!this.value.hasOwnProperty('country') || !this.value.country) {
			this.value.country = (this.value.hasOwnProperty('code') && this.value.code.length > 0) ?
				_find(this.countries, ['value', this.value.code]).key : this.country
		}
		if (!this.value.hasOwnProperty('code') || !this.value.code) {
			this.value.code = _find(this.countries, ['key', this.value.country]).value
		}
		if (!this.value.hasOwnProperty('number')) {
			this.value.number = ''
		}
	},
	mounted() {
		this.$refs.input.value = this.format(
			this.value.number.toString() + (this.value && this.value.ext ? 'x' + this.value.ext.toString() : '')
		)
		this.$refs.select.value = this.value.country
	},
	methods: {
		blur(event) {
			if (this.validateOnBlur == true) {
				this.$validate(event);
			}
		},
		format(number) {
			let reg = this.extension ? '[^x0-9]' : '\D'
			let value = number.replace(new RegExp(reg, 'g'), '')
			try {
				value = this.lib.formatInOriginalFormat(this.lib.parseAndKeepRawInput(value, this.value.country))
				if (this.extension && number.endsWith('x') && !value.endsWith('x')) {
					value = value + 'x'
				}
			} catch (error) { }
			return value
		},
		update(code, number, country) {
			let reg = this.extension ? '[^x0-9]' : '\D'
			let value = code + number.replace(new RegExp(reg, 'g'), '')
			let ext = ''
			try {
				let v = this.lib.parseAndKeepRawInput(value, country)
				ext = this.extension ? v.getExtension() : ''
				value = this.lib.format(v, this.formats.E164)
			} catch (error) { }
			this.value = { code: code, number: value.replace(code, ''), country: country, ext: ext ? ext : '' }
		},
		input(event) {
			this.$refs.input.value = this.format(event.target.value)
			try {
				let number = this.lib.parseAndKeepRawInput(event.target.value, this.value.country)
				let valid = this.lib.isValidNumber(number)
				if (this.lib.isPossibleNumber(number) && number.getCountryCode() == 1 && number.getNationalNumber().toString().slice(0, 3) == '826' && number.getNationalNumber().toString().length == 10) {
					valid = true
				}
				if (valid) {
					this.update(this.value.code, event.target.value, this.value.country)
				}
			} catch (error) { }
		},
		change(event) {
			let country = _find(this.countries, ['key', event.target.value])
			if (country && country.value) {
				this.update(country.value, '', event.target.value)
				this.value.code = country.value
				this.value.country = event.target.value
				if (!this.isClosed) { this.$refs.input.focus() }
			}
		},
		validate(event) {
			if (event.target.value.length > 0) {
				let valid = false
				try {
					let number = this.lib.parseAndKeepRawInput(event.target.value, this.value.country)
					valid = this.lib.isValidNumber(number)
					if (this.lib.isPossibleNumber(number) && number.getCountryCode() == 1 && number.getNationalNumber().toString().slice(0, 3) == '826' && number.getNationalNumber().toString().length == 10) {
						valid = true
					}
				} catch (error) { }
				if (valid) {
					this.update(this.value.code, event.target.value, this.value.country)
					event.target.setCustomValidity('')
				} else {
					event.target.setCustomValidity(this.$t('validation.regex', { attribute: this.placeholder }))
				}
			} else if (this.required == false) {
				this.value = { code: '', number: '', country: '', ext: '' }
			}
		},
		toggle(isClosed) {
			_each(this.$refs.select.options, function (o) {
				o.textContent = isClosed ?
					o.getAttribute('code') :
					o.getAttribute('title') + ' (' + o.getAttribute('code') + ')'
			})
			this.isClosed = isClosed
		}
	}
}
</script>
